(function shippingProtectionController(root) {
  'use strict';

  const protectionInputSelector = '.cart-insurance-input';
  const protectionProductHandle = 'shipping-protection-1';
  const protectionPollSchedule = [0, 50, 100, 150, 225, 325, 475, 700, 1000, 1400, 1900, 2500];
  let changeListenerAttached = false;
  let syncSequence = 0;

  function cartApiPath(path) {
    const routeRoot = root.Shopify?.routes?.root || '/';
    const normalizedRoot = routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`;
    const normalizedPath = String(path || '').replace(/^\//, '');

    return `${normalizedRoot}${normalizedPath}`;
  }

  function cartHasProtection(cart) {
    return Array.isArray(cart?.items)
      && cart.items.some((item) => item?.handle === protectionProductHandle);
  }

  function formatAmount(cents, precision, separator, delimiter) {
    const numericCents = Number(cents || 0);
    const sign = numericCents < 0 ? '-' : '';
    const amount = (Math.abs(numericCents) / 100).toFixed(precision);
    const parts = amount.split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
    const decimals = parts[1] ? separator + parts[1] : '';

    return sign + dollars + decimals;
  }

  function formatCartMoney(cents) {
    if (root.Shopify && typeof root.Shopify.formatMoney === 'function') {
      return String(root.Shopify.formatMoney(cents)).replace(/<[^>]*>/g, '');
    }

    const format = root.theme?.moneyFormat || '${{amount}}';
    const values = {
      amount: formatAmount(cents, 2, '.', ','),
      amount_no_decimals: formatAmount(cents, 0, '.', ','),
      amount_with_comma_separator: formatAmount(cents, 2, ',', '.'),
      amount_no_decimals_with_comma_separator: formatAmount(cents, 0, ',', '.'),
    };

    return String(format)
      .replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => values[token] || values.amount)
      .replace(/<[^>]*>/g, '');
  }

  function syncCartSummary(cart) {
    if (!root.document || !cart || typeof cart.total_price === 'undefined') {
      return;
    }

    root.document.querySelectorAll?.('[data-cart-subtotal]').forEach((subtotal) => {
      subtotal.textContent = formatCartMoney(cart.total_price);
    });
  }

  function setProtectionPending(input, pending) {
    const widget = input?.closest?.('.shipping-protection');
    if (!widget) {
      return;
    }

    widget.toggleAttribute?.('aria-busy', pending);
    widget.toggleAttribute?.('data-shipping-protection-pending', pending);
  }

  function dispatchProtectionUpdated(cart) {
    if (!root.document || typeof root.CustomEvent !== 'function') {
      return;
    }

    syncCartSummary(cart);
    root.document.dispatchEvent(new root.CustomEvent('cart:updated', {
      detail: {
        cart,
        openDrawer: false,
        source: 'shipping-protection',
      },
    }));

    if (typeof root.dispatchEvent === 'function') {
      root.dispatchEvent(new root.CustomEvent('shipping-protection:updated', {
        detail: { cart },
      }));
    }
  }

  async function syncProtectionCart(shouldHaveProtection, input = null) {
    if (typeof root.fetch !== 'function') {
      return null;
    }

    const sequence = ++syncSequence;
    let previousPollTime = 0;
    setProtectionPending(input, true);

    try {
      for (const pollTime of protectionPollSchedule) {
        const delay = pollTime - previousPollTime;
        previousPollTime = pollTime;
        if (delay > 0) {
          await new Promise((resolve) => root.setTimeout(resolve, delay));
        }

        let response;
        let cart;

        try {
          response = await root.fetch(cartApiPath('/cart.js'), {
            headers: { Accept: 'application/json' },
          });
          cart = await response.json().catch(() => null);
        } catch (_error) {
          continue;
        }

        if (sequence !== syncSequence) {
          return null;
        }

        if (response.ok && cart && cartHasProtection(cart) === shouldHaveProtection) {
          dispatchProtectionUpdated(cart);
          return cart;
        }
      }

      return null;
    } finally {
      if (sequence === syncSequence) {
        setProtectionPending(input, false);
      }
    }
  }

  function handleProtectionChange(event) {
    const input = event?.target?.closest?.(protectionInputSelector);
    if (!input) {
      return;
    }

    root.setTimeout(() => {
      syncProtectionCart(Boolean(input.checked), input).catch(() => null);
    }, 0);
  }

  function attachProtectionListener() {
    if (changeListenerAttached || typeof root.document?.addEventListener !== 'function') {
      return;
    }

    root.document.addEventListener('change', handleProtectionChange);
    changeListenerAttached = true;
  }

  function configureShippingProtection() {
    const template = root.document?.querySelector('[data-shipping-protection-template]');
    if (!template) {
      return false;
    }

    root.simplyInsurance = root.simplyInsurance || {};
    root.simplyInsurance.mode = 2;
    root.simplyInsurance.html = template.innerHTML.trim();

    if (typeof root.simplyInsurance.loadApp === 'function') {
      root.simplyInsurance.loadApp();
    }

    return true;
  }

  root.configureShippingProtection = configureShippingProtection;
  attachProtectionListener();
  configureShippingProtection();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      cartHasProtection,
      configureShippingProtection,
      formatCartMoney,
      protectionPollSchedule,
      syncCartSummary,
      syncProtectionCart,
    };
  }
}(typeof window !== 'undefined' ? window : globalThis));
